import React, { ComponentProps, memo, ReactNode, useState } from 'react'

import { isFunction } from '@surglogs/util.is-function'

import { useMemoizedCall } from '@surglogs-app/hooks'
import {
  BackArrowButton,
  FlexBox,
  Title,
  Text,
  TabListHeader,
  useOffsetManagerContext,
} from '@surglogs-app/ui'

import { ModalBackground } from './ModalBackground'
import { ModalContent, ModalSize } from './ModalContent'
import { deriveTabs } from './services/deriveTabs'

export interface ModalProps {
  title: ReactNode
  titleBreadcrumb?: ReactNode
  headerChildren?: ReactNode
  hideModal?: () => void
  tabs?: string[]
  children: (({ currentTab }: { currentTab: number }) => ReactNode) | ReactNode
  role?: string
  'aria-label'?: string
  description?: ReactNode
  headerRef?: React.RefObject<HTMLDivElement | null>
  onTabChange?: (tab: string, index: number) => void
  size?: ModalSize
  backgroundProps?: ComponentProps<typeof ModalBackground>
}

export const Modal = memo(
  ({
    title,
    titleBreadcrumb,
    hideModal,
    children,
    headerChildren,
    tabs,
    role = 'dialog',
    'aria-label': label = 'modal',
    description,
    headerRef,
    onTabChange,
    size = 'default',
    backgroundProps,
    ...rest
  }: ModalProps) => {
    const [currentTab, setCurrentTab] = useState(0)
    const { top: offset } = useOffsetManagerContext()
    const derivedTabs = useMemoizedCall(deriveTabs, [
      tabs || [],
      setCurrentTab,
      currentTab,
      onTabChange,
    ])

    return (
      <ModalBackground
        {...{ role, 'aria-label': label, offset, ...backgroundProps }}>
        <ModalContent {...{ ...rest, size }}>
          <FlexBox
            {...{
              flex: '1',
              ai: 'center',
              bb: tabs ? 'none' : 'solid',
              bw: tabs ? 'abs.0' : 'xxxs',
              bc: 'navy.10',
              ref: headerRef,
              padding: 'xl',
              className: 'modal-header',
            }}>
            <FlexBox {...{ ai: 'center', flex: 1 }}>
              {typeof hideModal === 'function' ? (
                <BackArrowButton {...{ onClick: hideModal, mr: 'md' }} />
              ) : null}
              <FlexBox
                {...{
                  flex: '1',
                  fd: 'column',
                  ai: 'start',
                  gap: 'xs',
                }}>
                {titleBreadcrumb ? (
                  <Text {...{ size: 'xxs' }}>{titleBreadcrumb}</Text>
                ) : null}
                <Title {...{ size: 'md' }}>{title}</Title>
                {description ? (
                  <Text {...{ size: 'xxs' }}>{description}</Text>
                ) : null}
              </FlexBox>
            </FlexBox>
            {headerChildren ? (
              <FlexBox
                {...{
                  flex: titleBreadcrumb ? 'none' : '1',
                  jc: 'flex-end',
                  ai: 'center',
                }}>
                {headerChildren}
              </FlexBox>
            ) : null}
          </FlexBox>
          {tabs ? <TabListHeader {...{ tabs: derivedTabs }} /> : null}
          {tabs && isFunction(children) ? children({ currentTab }) : null}
          {!isFunction(children) ? children : null}
        </ModalContent>
      </ModalBackground>
    )
  },
)
